import { PlayerAPI } from 'bitmovin-player';
import { PlaybackTimeLabel, UIInstanceManager } from 'bitmovin-player-ui';
import { __ } from '../../../translations';

/**
 * Updates the text content of a playback time label element to reflect live playback state.
 *
 * @param playbackTimeLabelElement - The playback time label HTMLElement whose text will be updated.
 */
const setLiveBtnText = (playbackTimeLabelElement: HTMLElement) => {
    if (!playbackTimeLabelElement || !playbackTimeLabelElement.classList.contains('bmpui-ui-playbacktimelabel-live')) {
        return;
    }

    if (playbackTimeLabelElement.classList.contains('bmpui-ui-playbacktimelabel-live-edge')) {
        playbackTimeLabelElement.innerText = __('player_lables_Live');
    } else {
        playbackTimeLabelElement.innerText = __('player_lables_go_live');
    }
};

/**
 * Extended PlaybackTimeLabel that updates the "Live" button text when the application's language changes.
 */
export class PlaybackTimeLabelLocalized extends PlaybackTimeLabel {
    configure(player: PlayerAPI, uimanager: UIInstanceManager): void {
        super.configure(player, uimanager);
        document.addEventListener('languageSwitched', this.setLiveBtnTextCallback);
    }

    setLiveBtnTextCallback = () => {
        setLiveBtnText(this.getDomElement().get(0));
    };

    release(): void {
        super.release();
        document.removeEventListener('languageSwitched', this.setLiveBtnTextCallback);
    }
}

// export const playbackTimeLabel = new PlaybackTimeLabelLocalized({
//     timeLabelMode: PlaybackTimeLabelMode.CurrentAndTotalTime,
//     cssClasses: ['ui-liveButton'],
// });

/**
 * Initializes a MutationObserver that keeps the playback time label's text in sync with its "live edge" state.
 *
 * The observer watches the PlaybackTimeLabel for changes to its `class` attribute. When the element receives the CSS class
 * `bmpui-ui-playbacktimelabel-live-edge`, the observer sets the element's inner text to the localized "Live"
 * label. When that class is not present, it sets the text to the localized "Go Live" label.
 *
 * @todo Consider integrating this functionality directly into the PlaybackTimeLabelLocalized class.
 *
 * @param playbackTimeLabel - The PlaybackTimeLabelLocalized instance to observe.
 */
export const initPlaybackTimeLabelLiveObserver = (playbackTimeLabel: PlaybackTimeLabelLocalized) => {
    if (!playbackTimeLabel) {
        return;
    }

    const playbackTimeLabelElement = playbackTimeLabel.getDomElement().get(0);

    if (!playbackTimeLabelElement) {
        return;
    }

    const ob = new MutationObserver((mutations: MutationRecord[]) => {
        mutations.forEach((mutation: MutationRecord) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const target = mutation.target as HTMLElement;
                setLiveBtnText(target);
            }
        });
    });

    ob.observe(playbackTimeLabelElement, { attributes: true, attributeFilter: ['class'] });

    // Disconnect the observer when the playbackTimeLabel component is released by UIManager to avoid memory leaks.
    playbackTimeLabel.release = () => ob.disconnect();
};
